import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

/**
 * Header ⋯ menu portaled to body so sticky headers cannot clip it.
 */
export function ProjectOverflowMenu({ items = [] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const visible = items.filter((item) => item && item.hidden !== true);

  const place = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const width = 192;
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    setPos({ top: rect.bottom + 8, left });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onScroll = () => place();
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, place]);

  if (visible.length === 0) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="pv-btn pv-btn-icon"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("More")}
        onClick={() => setOpen((value) => !value)}
      >
        <EllipsisVerticalIcon className="h-5 w-5" aria-hidden />
      </button>
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[80]" onClick={() => setOpen(false)} />
            <div
              role="menu"
              className="fixed z-[81] w-48 rounded-[11px] border border-[#dfe5ec] bg-white p-1.5 shadow-[0_12px_32px_rgba(20,35,55,.14)]"
              style={{ top: pos.top, left: pos.left }}
            >
              {visible.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.label}
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-[#33445a] hover:bg-[#f2f5f9]"
                    onClick={() => {
                      setOpen(false);
                      item.action?.();
                    }}
                  >
                    {Icon ? <Icon className="h-5 w-5 text-gray-400" aria-hidden /> : null}
                    {item.label}
                  </button>
                );
              })}
            </div>
          </>,
          document.body
        )}
    </>
  );
}

export default ProjectOverflowMenu;
